import { useState } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/lib/auth-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

function RizomaLogo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Rizoma"
    >
      <rect width="44" height="44" rx="9" fill="#2D5016" />
      <circle cx="14" cy="22" r="4.5" fill="white" />
      <circle cx="27" cy="14" r="3.5" fill="white" opacity="0.75" />
      <circle cx="27" cy="30" r="3.5" fill="white" opacity="0.75" />
      <circle cx="37" cy="22" r="2.5" fill="#C17BC0" opacity="0.9" />
      <line x1="14" y1="22" x2="24" y2="15" stroke="white" strokeWidth="1.4" opacity="0.5" />
      <line x1="14" y1="22" x2="24" y2="29" stroke="white" strokeWidth="1.4" opacity="0.5" />
      <line x1="27" y1="14" x2="35" y2="22" stroke="#C17BC0" strokeWidth="1.2" opacity="0.5" />
      <line x1="27" y1="30" x2="35" y2="22" stroke="#C17BC0" strokeWidth="1.2" opacity="0.5" />
    </svg>
  );
}

export default function SignupPage() {
  const { login } = useAuth();
  const [, setLocation] = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const formData = new FormData(e.currentTarget);
    const body = {
      nomeFazenda: formData.get('nomeFazenda') as string,
      nomeResponsavel: formData.get('nomeResponsavel') as string,
      email: formData.get('email') as string,
      password: formData.get('password') as string,
    };

    try {
      const res = await fetch('/api/organizations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      login(data.user);
      setLocation('/inicio');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">
            <RizomaLogo className="h-12 w-12" />
          </div>
          <CardTitle className="text-2xl">Criar sua conta</CardTitle>
          <CardDescription>Gratuito para produtores. Sem cartão de crédito.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="nomeFazenda">Nome da fazenda</Label>
              <Input
                id="nomeFazenda"
                name="nomeFazenda"
                placeholder="Ex: Fazenda São José"
                required
                minLength={2}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="nomeResponsavel">Seu nome</Label>
              <Input
                id="nomeResponsavel"
                name="nomeResponsavel"
                placeholder="Ex: João Silva"
                required
                minLength={2}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="seu@email.com"
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="Mínimo 6 caracteres"
                required
                minLength={6}
              />
            </div>
            {error && <p className="text-sm text-erro">{error}</p>}
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Criando conta...' : 'Criar conta grátis'}
            </Button>
          </form>
          <p className="text-center text-sm text-muted-foreground mt-4">
            Já tem conta?{' '}
            <a href="/login" className="text-primary hover:underline">
              Entrar
            </a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
